import { Button, Input, Select } from "antd";
import { TableProps } from "antd/es/table";
import moment from "moment";
import { useState } from "react";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import CustomTable from "../../../components/ui/CustomTable";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import { useGetPettyCashQuery } from "../../../features/pettyCash/pettyCash";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import {
  formatDate,
  formatTime,
  isExpiredWithoutToday,
} from "../../../lib/time";
import { GetPettyCashParams } from "../../../types/pettyCash";
import { countParPageSerialNumber } from "../../../utils/number";
import {
  addKeyInArray,
  formatSouthAsianWithDecimals,
  selectSearchOption,
  underscoreToCapitalizeString,
} from "../../../utils/utils";
import PettyCashDetails from "./_components/PettyCashDetails";
import SendBulkDisbursement from "./_components/SendBulkDisbursement";

export const pettyCashStatusList = {
  approved: {
    text: "Approved",
    class: "status text-green-700 bg-green-50",
  },
  rejected: {
    text: "Rejected",
    class: "status text-red-700 bg-red-50",
  },
  pending: {
    text: "Pending",
    class: "status text-yellow-700 bg-yellow-50",
  },
};

const statusArray: { label: string; value: string }[] = Object.keys(
  pettyCashStatusList
).map((key) => ({
  value: key,
  label: pettyCashStatusList[key as keyof typeof pettyCashStatusList].text,
}));

export const pettyCashPaymentStatusList = {
  completed: {
    text: "Paid",
    class: "status text-green-700 bg-green-50",
  },
  send_for_disbursement: {
    text: "Send for Disbursement",
    class: "status text-orange-600 bg-orange-50",
  },
  failed: {
    text: "Failed",
    class: "status text-red-700 bg-red-50",
  },
  pending: {
    text: "Pending",
    class: "status text-yellow-700 bg-yellow-50",
  },
};

const statusPaymentArray: { label: string; value: string }[] = Object.keys(
  pettyCashPaymentStatusList
).map((key) => ({
  value: key,
  label:
    pettyCashPaymentStatusList[key as keyof typeof pettyCashPaymentStatusList]
      .text,
}));

export type SelectedDataType = {
  amount: number;
  id: string;
  isExpire: boolean;
}[];

export default function PettyCash() {
  const { open, handleClose, handleOpen } = useModal<
    SelectedDataType | number,
    "BULK_DISBURSEMENT" | "DOWNLOAD"
  >(reRender);
  const [selectedData, setSelectedData] = useState<SelectedDataType>([]);
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    keyword,
    handleSearch,
    filter,
    handleFilter,
  } = useTable<GetPettyCashParams>();
  const { isLoading, isFetching, isError, error, data } = useGetPettyCashQuery({
    params: {
      ...filter,
      search: keyword,
    },
  });
  useError(isError, error, "Get petty cash data fail");

  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: () => (
        <span>
          Employee <br /> Name
        </span>
      ),
      dataIndex: "business_member",
      sorter: (a, b) =>
        a?.business_member?.member?.profile?.name?.localeCompare(
          b?.business_member?.member?.profile?.name
        ),
      sortIcon: () => <SortIcon />,
      render: (val, record) => val?.member?.profile?.name ?? "-",
    },
    {
      title: () => (
        <span>
          Request Date <br /> & Time
        </span>
      ),
      dataIndex: "created_at",
      render: (val) =>
        val ? (
          <span className="block min-w-[120px]">
            {formatDate(val)} <br />
            {formatTime(val)}
          </span>
        ) : (
          "-"
        ),
    },
    // {
    //   title: "Request ID",
    //   dataIndex: "start_date",
    // },
    {
      title: "Request Title",
      dataIndex: "title",
      width: 250,
    },
    {
      title: "Wallet Number",
      dataIndex: "wallet_number",
      render: (val) => val || "-",
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      width: 180,
      render: (val) =>
        val ? (
          <span className="block min-w-[120px]">
            {moment.utc(val, dbDateFormat).format(dateFormat)}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (val) => `৳${formatSouthAsianWithDecimals(Number(val))}`,
    },
    // {
    //   title: "Txn ID",
    //   dataIndex: "unique_batch_code",
    //   render: (val) => val || "-",
    // },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      render: (val: keyof typeof pettyCashStatusList) => (
        <span className={pettyCashStatusList?.[val]?.class}>
          {pettyCashStatusList?.[val]?.text ||
            underscoreToCapitalizeString(val || "-")}
        </span>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      align: "center",
      render: (val: keyof typeof pettyCashPaymentStatusList) => (
        <span className={pettyCashPaymentStatusList?.[val]?.class}>
          {pettyCashPaymentStatusList?.[val]?.text ||
            underscoreToCapitalizeString(val || "-")}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val) => (
        <Button
          className="action-button"
          size="small"
          icon={<ViewIcon />}
          onClick={() =>
            handleOpen({
              type: "VIEW",
              data: val,
            })
          }
        />
      ),
    },
  ];

  function reRender() {
    setSelectedData([]);
  }

  return (
    <>
      <SidebarLayout title="Petty Cash">
        <CustomTable
          loading={isLoading || isFetching}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.totalLength}
          dataSource={addKeyInArray(data?.data?.pettyCash)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedData?.map((item) => item?.id),
            onChange: (_, rows) => {
              setSelectedData(
                rows.map((item) => ({
                  id: item.id,
                  amount: item.amount,
                  isExpire: isExpiredWithoutToday(item?.due_date),
                }))
              );
            },
            getCheckboxProps: (record) => {
              return {
                disabled:
                  record?.status === "approved" &&
                  record?.payment_status === "pending"
                    ? false
                    : true,
              };
            },
            columnWidth: 40,
          }}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search by title"
                onChange={handleSearch}
                className="min-w-[150px]"
              />
              <Select
                allowClear
                showSearch
                onChange={(val) => handleFilter("status", val)}
                placeholder="Status"
                filterOption={selectSearchOption}
                options={statusArray}
                className="min-w-[110px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
              />

              <Select
                allowClear
                showSearch
                onChange={(val) => handleFilter("payment_status", val)}
                placeholder="Payment Status"
                filterOption={selectSearchOption}
                options={statusPaymentArray}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                popupMatchSelectWidth={190}
              />

              <Button
                type="primary"
                className="middle-button disabled:!text-white disabled:opacity-30 px-3"
                onClick={() => {
                  if (selectedData?.length)
                    handleOpen({
                      type: "BULK_DISBURSEMENT",
                      data: selectedData,
                    });
                }}
                disabled={!selectedData?.length}
              >
                Send for Disbursement
              </Button>
            </>
          }
        />

        {open.type === "BULK_DISBURSEMENT" &&
          typeof open?.data === "object" && (
            <SendBulkDisbursement
              open={open.open}
              data={open?.data}
              handleClose={handleClose}
            />
          )}

        {open.type === "VIEW" && typeof open?.data === "number" && (
          <PettyCashDetails
            open={open.open}
            data={open?.data}
            handleClose={handleClose}
          />
        )}
      </SidebarLayout>
    </>
  );
}
